// ============================================================================
// STATISTIQUES › VENTES › REPORTING ARTICLES — la vie d'un article : ce qu'on en a reçu, ce qu'on en a vendu, ce qu'il
// en reste, jusqu'au gencode. Réf. maquette du back-office (« Reporting article ») et
// docs/CADRAGE-Statistiques-Ventes.md §8.
//
// ⚠️ L'ÉCRAN TRIE ET DESSINE, IL NE DÉFINIT AUCUN NOMBRE : tout vient du serveur (`sales/stats-articles.ts`), le
//    squelette du tableau est celui de tous les reportings (`stats-grid.tsx`).
// ⚠️ DEUX FENÊTRES DE NATURE DIFFÉRENTE : les colonnes REÇUES suivent la fenêtre Réceptions (bleu), tout le reste la
//    fenêtre Ventes — et « reçu » se DIT : toute entrée achetée, pas seulement les bons de livraison.
// ⚠️ UN SEUL COEF, du prix d'achat HT au prix TTC, comme partout dans le produit.
// ============================================================================
import { Trans } from '@orgazone/ui-web';
import { currentCurrency, toInputValue } from '@orgazone/money-format';
import type { ArticleReportAxis, ArticleReportMeasures, ArticleReportMetric, ArticleReportView } from '../api';
import { key, t, type Translatable } from '../i18n';
import { fieldLabel } from '../vocabulary';
import type { VocabularyFieldKey } from '@orgazone/sync-contract';
import { exporterCsv } from './export-csv';
import { ARTICLE_LEVEL_DIMS, dimLabel } from './stats-bar';
import { coef, csvCoef, csvQty, csvRate, eur0, pc, qty, shownValue } from './stats-format';
import { StatsGrid, sortStatsRows, useStatsColumns, type GridCell, type StatsSort } from './stats-grid';

/** Le tri par défaut : les ventes HT, du plus gros au plus petit, une fois l'ordre des niveaux respecté. */
export const ARTICLE_DEFAULT_SORT: StatsSort = { k: 'netHtCents', d: -1, man: false };

/** Les niveaux par défaut : l'article (le modèle). */
export const ARTICLE_DEFAULT_AXES: ArticleReportAxis[] = ['art'];

type Block = 'sales' | 'receptions';
const BLOCKS: Record<Block, { label: Translatable; color: string }> = {
  sales: { label: key('Ventes'), color: 'var(--teal-500)' },
  receptions: { label: key('Réceptions'), color: 'var(--blue)' },
};

/** Ce que « reçu » compte — dit en tête de chaque colonne reçue et au pied du tableau. */
const RECEIVED_HELP = key('Suit la fenêtre Réceptions. « Reçu » compte toutes les entrées achetées : bons de livraison, rétrocessions entrantes et entrées reprises valorisées, à leur coût d’entrée.');

type Unit = 'money' | 'rate' | 'qty' | 'coef' | 'date';
interface ArticleColumn {
  k: ArticleReportMetric;
  label: Translatable;
  /** Le champ de la fiche dont la colonne porte le nom — le MOT DU MAGASIN (`fieldLabel`). */
  field?: VocabularyFieldKey;
  unit: Unit;
  width: number;
  block: Block;
}

/** Les colonnes, dans l'ordre de la maquette — un seul « Coef », sur le TTC. */
export const ARTICLE_COLUMNS: ArticleColumn[] = [
  { k: 'lastReception', label: key('Dern. réception'), unit: 'date', width: 116, block: 'receptions' },
  { k: 'lastSale', label: key('Dern. vente'), unit: 'date', width: 108, block: 'sales' },
  { k: 'receivedQty', label: key('Qté reçue'), unit: 'qty', width: 88, block: 'receptions' },
  { k: 'receivedHtCents', label: key('Reçu {symbole} HT'), unit: 'money', width: 104, block: 'receptions' },
  { k: 'soldCostHtCents', label: key('Vendu {symbole} HT ({champ})'), field: 'purchasePriceExclTax', unit: 'money', width: 116, block: 'sales' },
  { k: 'qtySold', label: key('Qté vendue'), unit: 'qty', width: 92, block: 'sales' },
  { k: 'netHtCents', label: key('Ventes {symbole} HT'), unit: 'money', width: 108, block: 'sales' },
  { k: 'netTtcCents', label: key('Ventes {symbole} TTC'), unit: 'money', width: 110, block: 'sales' },
  { k: 'stockQty', label: key('Qté stock'), unit: 'qty', width: 88, block: 'sales' },
  { k: 'stockValueHtCents', label: key('Stock {symbole} HT'), unit: 'money', width: 104, block: 'sales' },
  { k: 'receivedSoldBp', label: key('% reçu / vendu'), unit: 'rate', width: 104, block: 'receptions' },
  { k: 'marginHtCents', label: key('Marge {symbole} HT'), unit: 'money', width: 106, block: 'sales' },
  { k: 'marginRateBp', label: key('Marge %'), unit: 'rate', width: 88, block: 'sales' },
  { k: 'coefBp', label: key('Coef'), unit: 'coef', width: 86, block: 'sales' },
];

/** La colonne qui ne se masque jamais — la quantité vendue, comme dans la maquette. */
export const ARTICLE_KEY_COLUMN: ArticleReportMetric = 'qtySold';

/** Les colonnes que la fenêtre Réceptions gouverne. */
const RECEIVED: readonly ArticleReportMetric[] = ['lastReception', 'receivedQty', 'receivedHtCents', 'receivedSoldBp'];

/** Le libellé d'une colonne — la devise et le nom du champ sont ceux du magasin. */
const columnLabel = (c: ArticleColumn): string =>
  t(c.label, { symbole: currentCurrency().symbol, ...(c.field ? { champ: fieldLabel(c.field) } : {}) });

/** Les colonnes choisies de ce tableau, mémorisées sur le profil. */
export function useArticleColumns(enabled: boolean, onSaveFailed: (message: string) => void) {
  return useStatsColumns('stats-ventes-articles', ARTICLE_COLUMNS, ARTICLE_KEY_COLUMN, columnLabel, enabled, onSaveFailed);
}

/** La légende des deux blocs dont au moins une colonne est affichée. */
export const articleLegend = (visible: ArticleColumn[]): { label: string; color: string }[] =>
  (Object.keys(BLOCKS) as Block[]).filter((b) => visible.some((c) => c.block === b))
    .map((b) => ({ label: t(BLOCKS[b].label), color: BLOCKS[b].color }));

/** Ce qu'une ligne compte, selon le niveau le plus fin. */
const unitsLabel = (grain: ArticleReportView['grain'], n: number): string =>
  grain === 'ean' ? t('{n} déclinaison', { n }) : grain === 'coul' ? t('{n} coloris', { n }) : t('{n} article', { n });

/** L'en-tête de la colonne qui compte les unités, dans un fichier exporté. */
const unitsHeader = (grain: ArticleReportView['grain']): string =>
  grain === 'ean' ? t('Déclinaisons') : grain === 'coul' ? t('Coloris|en-tête de colonne') : t('Articles');

/** « 42 articles · 18 lignes » — ou « … · 25 combinaisons » quand il y a plusieurs niveaux. */
export function articleScope(view: ArticleReportView): string {
  const rows = view.axes.length > 1 ? t('{n} combinaison', { n: view.scope.rows }) : t('{n} ligne', { n: view.scope.rows });
  return t('{articles} · {lignes}', { articles: unitsLabel(view.grain, view.scope.units), lignes: rows });
}

// ─── Les cellules ────────────────────────────────────────────────────────────────────────────────

/** Une date `AAAAMMJJ` au format court de la langue du compte. */
function shortDate(ymd: string, lang: string): string {
  const d = new Date(Number(ymd.slice(0, 4)), Number(ymd.slice(4, 6)) - 1, Number(ymd.slice(6, 8)));
  return new Intl.DateTimeFormat(lang, { day: '2-digit', month: '2-digit', year: '2-digit' }).format(d);
}

/** Le texte d'une valeur selon son unité ; « — » quand elle ne se mesure pas. */
function formatValue(v: number | string | null, unit: Unit, lang: string): string {
  if (v === null) return '—';
  if (unit === 'date') return shortDate(String(v), lang);
  if (unit === 'money') return eur0(v as number);
  if (unit === 'rate') return pc(v as number);
  if (unit === 'coef') return coef(v as number);
  return qty(v as number);
}

/**
 * Le texte, la teinte et la graisse d'une cellule — les seuils de la maquette : marge sous 25 % en rose, reçu sur vendu
 * au-dessus de 150 % en ambre (on rentre plus qu'on n'écoule), jusqu'à 100 % en vert.
 */
function cell(m: ArticleReportMeasures, c: ArticleColumn, lang: string): GridCell {
  const v = m[c.k];
  const n = typeof v === 'number' ? v : null;
  const fg = c.k === 'marginRateBp' && n !== null && n < 2500 ? 'var(--rose-fg)'
    : c.k === 'receivedSoldBp' && n !== null && n > 15000 ? 'var(--amber-fg)'
      : c.k === 'receivedSoldBp' && n !== null && n <= 10000 ? 'var(--teal-800)'
        : v === null && c.unit !== 'date' ? 'var(--ink-300)' : 'var(--ink-800)';
  return { text: formatValue(v, c.unit, lang), fg, fw: c.k === 'netHtCents' || c.k === 'marginHtCents' ? 700 : 600 };
}

type Row = ArticleReportView['rows'][number];

/** Les lignes dans l'ordre demandé — à égalité de niveaux, les plus grosses ventes HT d'abord. */
export function sortArticleRows(rows: Row[], axes: readonly ArticleReportAxis[], sort: StatsSort, lang: string): Row[] {
  return sortStatsRows(rows, axes.length, sort, (r, k) => r.m[k as ArticleReportMetric], (x, y) => y.m.netHtCents - x.m.netHtCents, lang);
}

// ─── Le tableau ──────────────────────────────────────────────────────────────────────────────────

/** LE TABLEAU DU REPORTING ARTICLES — ses colonnes et ses cellules, dans le squelette commun des reportings. */
export function StatsArticlesTable({ view, visible, sort, onSort, filtered, onReset, lang, busy = false }: {
  view: ArticleReportView;
  visible: ArticleColumn[];
  sort: StatsSort;
  onSort: (next: StatsSort) => void;
  filtered: boolean;
  onReset: () => void;
  lang: string;
  busy?: boolean;
}) {
  const rows = sortArticleRows(view.rows, view.axes, sort, lang);
  const levelDim = (k: ArticleReportAxis) => ARTICLE_LEVEL_DIMS.find((d) => d.k === k)!;
  const totalSub = `${t('{n} ligne', { n: view.rows.length })}${sort.man ? '' : ` · ${t('ordre de la ventilation')}`}`;
  return (
    <StatsGrid label={t('Reporting articles')} sort={sort} onSort={onSort} busy={busy}
      axes={view.axes.map((k) => ({ label: dimLabel(levelDim(k)), code: k === 'art' || k === 'ean' }))}
      columns={visible.map((c) => ({
        k: c.k, label: columnLabel(c), width: c.width, color: BLOCKS[c.block].color,
        ...(RECEIVED.includes(c.k) ? { title: t(RECEIVED_HELP) } : {}),
      }))}
      total={{ sub: totalSub, cells: visible.map((c) => cell(view.total, c, lang)) }}
      rows={rows.map((r) => ({
        key: r.parts.map((p) => p.value).join('\u001f'), parts: r.parts, sub: unitsLabel(view.grain, r.units),
        cells: visible.map((c) => cell(r.m, c, lang)),
      }))}
      after={(
        <>
          {view.rows.length === 0 && (
            <div style={{ padding: '34px 18px', textAlign: 'center' }}>
              {filtered ? (
                <>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucun article ne correspond à ces filtres')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>
                    <Trans k='Les filtres se cumulent en ET — <1>effacez-les</1> pour repartir.' parts={[(x) => (
                      <button type="button" onClick={onReset} style={{
                        border: 'none', background: 'transparent', padding: 0, color: 'var(--teal-800)', fontSize: 'var(--t-body)', fontWeight: 700,
                        textDecoration: 'underline', cursor: 'pointer',
                      }}>{x}</button>
                    )]} />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucune vente ni réception sur ces fenêtres')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 4 }}>{t('Choisissez d’autres fenêtres de ventes ou de réceptions.')}</div>
                </>
              )}
            </div>
          )}
          <div style={{
            padding: '11px 14px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-200)', borderRadius: '0 0 var(--r-sm) var(--r-sm)',
            fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.55,
          }}>
            <Trans k='<1>« Reçu » compte toutes les entrées achetées</1> de la fenêtre Réceptions — bons de livraison, rétrocessions entrantes et entrées reprises valorisées —, à leur coût d’entrée ; tout le reste suit la fenêtre Ventes. Le stock est celui d’aujourd’hui, valorisé au PMP, pour toutes les déclinaisons de la ligne. Le coef est <2>ventes TTC / prix d’achat HT</2>. Un indicateur non mesurable affiche « — », jamais zéro.'
              parts={[
                (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>,
                (x) => <span style={{ fontFamily: 'var(--font-mono)' }}>{x}</span>,
              ]} />
          </div>
        </>
      )} />
  );
}

// ─── L'export ────────────────────────────────────────────────────────────────────────────────────

/**
 * « ON EXPORTE CE QUE L'ON VOIT » : les niveaux, les colonnes affichées dans leur ordre, le tri, la ligne de total.
 * ⚠️ UN MONTANT INCONNU RESTE VIDE — un 0 dans un tableur devient une somme fausse ; une date s'écrit `AAAA-MM-JJ`,
 *    la seule qu'aucun tableur ne lit à l'envers.
 */
export function exportArticles(view: ArticleReportView, visible: ArticleColumn[], sort: StatsSort, lang: string): void {
  const value = (m: ArticleReportMeasures, c: ArticleColumn): string => {
    const x = m[c.k];
    if (x === null) return '';
    if (c.unit === 'date') { const s = String(x); return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`; }
    if (c.unit === 'rate') return csvRate(x as number);
    if (c.unit === 'money') return toInputValue(x as number);
    if (c.unit === 'coef') return csvCoef(x as number);
    return csvQty(x as number);
  };
  const head = (c: ArticleColumn) => (c.unit === 'rate' ? `${columnLabel(c)} (%)` : c.unit === 'money' ? `${columnLabel(c)} (${currentCurrency().code})` : columnLabel(c));
  const levelDim = (k: ArticleReportAxis) => ARTICLE_LEVEL_DIMS.find((d) => d.k === k)!;
  const rows = sortArticleRows(view.rows, view.axes, sort, lang);
  exporterCsv(`reporting-articles-${view.period.from}-${view.period.to}`,
    [...view.axes.map((k) => dimLabel(levelDim(k))), unitsHeader(view.grain), ...visible.map(head)],
    [
      [t('TOTAL'), ...view.axes.slice(1).map(() => ''), String(view.scope.units), ...visible.map((c) => value(view.total, c))],
      ...rows.map((r) => [...r.parts.map((p) => (p.label !== p.value ? `${p.value} — ${shownValue(p.label)}` : shownValue(p.value))), String(r.units), ...visible.map((c) => value(r.m, c))]),
    ]);
}
